import React from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Avatar, 
  Button, 
  Grid, 
  Chip,
  Stack,
  Divider,
  Alert
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
  PersonOutlined, 
  EmailOutlined, 
  BusinessOutlined,
  SecurityOutlined,
  LogoutOutlined,
  VerifiedOutlined
} from '@mui/icons-material';

const Dashboard = () => {
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();

  if (!isLoaded) {
    return (
      <Box sx={{ padding: '2rem' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const handleSignOut = () => {
    signOut();
  };

  return (
    <Box sx={{ padding: '2rem' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Welcome Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom color="primary.main">
            👋 Welcome to Autobia Design System
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px' }}>
            You now have access to our comprehensive design system documentation. 
            Explore components, foundations, and patterns used across Autobia applications.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* User Profile Card */}
          <Grid item xs={12} md={8}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    src={user?.imageUrl}
                    sx={{
                      width: 80,
                      height: 80,
                      mr: 3,
                      border: '3px solid',
                      borderColor: 'primary.main',
                    }}
                  >
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" fontWeight={600} gutterBottom>
                      {user?.fullName || 'Autobia Employee'}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                      <Chip 
                        icon={<VerifiedOutlined />}
                        label="Authorized User" 
                        color="success" 
                        variant="outlined" 
                        size="small"
                      />
                      <Chip 
                        icon={<BusinessOutlined />}
                        label="Autobia Team" 
                        color="primary" 
                        variant="outlined" 
                        size="small"
                      />
                    </Stack>
                  </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* User Details */}
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PersonOutlined sx={{ mr: 2, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Full Name
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {user?.fullName || 'Not provided'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <EmailOutlined sx={{ mr: 2, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Email Address
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {user?.primaryEmailAddress?.emailAddress}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <SecurityOutlined sx={{ mr: 2, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Account Status
                        </Typography>
                        <Typography variant="body1" fontWeight={500} color="success.main">
                          Verified & Active
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BusinessOutlined sx={{ mr: 2, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Organization
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          Autobia
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Quick Actions
                </Typography>
                <Stack spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={<PersonOutlined />}
                    onClick={() => user?.update()}
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Manage Profile
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<LogoutOutlined />}
                    onClick={handleSignOut}
                    color="error"
                    fullWidth
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Sign Out
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Security Notice */}
            <Alert 
              severity="info" 
              icon={<SecurityOutlined />}
              sx={{ borderRadius: 2 }}
            >
              <Typography variant="body2">
                <strong>Security Notice:</strong><br />
                Access is restricted to @autobia.sa email addresses only. 
                Your session is automatically secured.
              </Typography>
            </Alert>
          </Grid>

          {/* Navigation Help */}
          <Grid item xs={12}>
            <Card elevation={2} sx={{ borderRadius: 3, bgcolor: 'primary.light' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  🚀 Getting Started
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Explore our design system components and foundations using the navigation menu. 
                  Here are some recommended starting points:
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        🎨 Foundations
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Colors, Typography, Spacing
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        🔘 Components
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Buttons, Inputs, Data Display
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        💳 Financial
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Payment Cards, Stats, KPIs
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        📱 Patterns
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Layouts, Navigation, Modals
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </motion.div>
    </Box>
  );
};

export default Dashboard;